import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { useAuthHydrated, useAuthStore } from '../store'
import { RouteFallback } from '@/app/routes/route-fallback'
import type { UserRole } from '../types'

interface ProtectedRouteProps {
  /** If set, only users with one of these roles can pass. */
  roles?: UserRole[]
  redirectTo?: string
}

/**
 * Auth gate. Waits for the persisted store to rehydrate before deciding
 * — that prevents a flash redirect to /login when the user is actually
 * signed in but the store hasn't read localStorage yet.
 */
export function ProtectedRoute({
  roles,
  redirectTo = '/login',
}: ProtectedRouteProps) {
  const hydrated = useAuthHydrated()
  const user = useAuthStore((s) => s.user)
  const token = useAuthStore((s) => s.token)
  const location = useLocation()

  if (!hydrated) return <RouteFallback />

  if (!user || !token) {
    return (
      <Navigate
        to={redirectTo}
        replace
        state={{ from: location.pathname + location.search }}
      />
    )
  }

  if (roles && !roles.includes(user.role)) {
    return <Navigate to="/" replace />
  }

  return <Outlet />
}
